import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteUser, getUsers } from '../../../../api/endpoints/system';
import { USER_TYPE_LABELS, type SystemUser, type UserTypeCode } from '../../../../api/types/system';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../components/ListSearchField';
import { UserFormDialog } from '../UserFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this project's
 *  pinned TypeScript 4.9.5). */
export default function UserListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SystemUser | null>(null);
  const [deleting, setDeleting] = useState<SystemUser | null>(null);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['system', 'users'], queryFn: () => getUsers() });
  const rows: SystemUser[] = useMemo(() => query.data?.userList ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) => {
      const typeLabel = row.userType !== undefined ? USER_TYPE_LABELS[row.userType as UserTypeCode] : '';
      return [row.username, row.firstName, row.lastName, row.email, typeLabel, row.gsaId, row.isActive ? 'Aktif' : 'Pasif']
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term);
    });
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: () => {
      enqueueSnackbar('Kullanıcı silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['system', 'users'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Kullanıcılar</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Kullanıcı
        </Button>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Kullanıcı adı, ad soyad, e-posta..." />
      </Box>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kullanıcı Adı</TableCell>
              <TableCell>Ad Soyad</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Firma ID</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı kullanıcı bulunamadı.' : 'Aramayla eşleşen kullanıcı bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.username}</TableCell>
                <TableCell>
                  {row.firstName} {row.lastName}
                </TableCell>
                <TableCell>{row.email ?? '—'}</TableCell>
                <TableCell>
                  {row.userType !== undefined ? USER_TYPE_LABELS[row.userType as UserTypeCode] : '—'}
                </TableCell>
                <TableCell>{row.gsaId ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.isActive ? 'Aktif' : 'Pasif'}
                    color={row.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <UserFormDialog open={createOpen} user={null} onClose={() => setCreateOpen(false)} />
      <UserFormDialog open={!!editing} user={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Kullanıcıyı sil"
        description={`"${deleting?.username ?? ''}" kullanıcısını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
